import React from "react";
import {
  Dialog,
  DialogContent,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Progress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import {
  Target,
  Calendar,
  Banknote,
  TrendingUp,
  TrendingDown,
  PieChart,
  FileText,
  Plus,
  Edit,
  Trash2,
} from "lucide-react";
import { financeService } from "../../../services/financeService";

const BudgetDetailsModal = ({
  open,
  onOpenChange,
  budget,
  items,
  loading,
  onEdit,
  onManageItems,
  onDeleteItem,
  calculateProgress,
  getStatusColor,
}) => {
  if (!budget) return null;

  const progress = calculateProgress(budget);

  return (
    <>
      <style>{`
        .budget-details-modal-large {
          width: 98vw !important;
          max-width: 98vw !important;
          height: 95vh !important;
          max-height: 95vh !important;
          min-width: 98vw !important;
          min-height: 95vh !important;
        }
      `}</style>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="overflow-hidden p-0 budget-details-modal-large">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              {/* Header Section with Gradient */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 md:p-6">
                <div className="flex flex-col md:flex-row items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Target className="w-5 h-5 md:w-6 md:h-6" />
                      <h2 className="text-xl md:text-2xl font-bold">
                        {budget.name}
                      </h2>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 md:gap-4 text-blue-100 text-xs md:text-sm">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                        {new Date(
                          budget.period_start,
                        ).toLocaleDateString()} -{" "}
                        {new Date(budget.period_end).toLocaleDateString()}
                      </span>
                      <Badge
                        variant="outline"
                        className="bg-white/20 text-white border-white/30 capitalize text-xs"
                      >
                        {budget.type}
                      </Badge>
                      <Badge
                        className={`${getStatusColor(progress.status)} border-0 text-xs`}
                      >
                        {progress.status === "over"
                          ? "Over Budget"
                          : progress.status === "warning"
                            ? "Warning"
                            : "On Track"}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onOpenChange(false);
                        onEdit(budget);
                      }}
                      className="bg-white/10 text-white border-white/30 hover:bg-white/20 text-xs md:text-sm"
                    >
                      <Edit className="w-3 h-3 md:w-4 md:h-4 mr-1" /> Edit
                    </Button>
                  </div>
                </div>
              </div>

              {/* Content Section - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50">
                <div className="space-y-4 md:space-y-6">
                  {/* Financial Overview Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Budgeted Amount
                            </p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                              {financeService.formatCurrency(
                                budget.budgeted_amount,
                              )}
                            </p>
                          </div>
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <Banknote className="w-6 h-6 text-blue-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Actual Spent
                            </p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                              {financeService.formatCurrency(progress.spent)}
                            </p>
                          </div>
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-green-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card
                      className={`border-l-4 ${progress.remaining >= 0 ? "border-l-emerald-500" : "border-l-red-500"}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Remaining
                            </p>
                            <p
                              className={`text-2xl font-bold mt-1 ${progress.remaining >= 0 ? "text-emerald-600" : "text-red-600"}`}
                            >
                              {financeService.formatCurrency(
                                progress.remaining,
                              )}
                            </p>
                          </div>
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center ${progress.remaining >= 0 ? "bg-emerald-100" : "bg-red-100"}`}
                          >
                            {progress.remaining >= 0 ? (
                              <TrendingUp className="w-6 h-6 text-emerald-600" />
                            ) : (
                              <TrendingDown className="w-6 h-6 text-red-600" />
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-purple-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Utilization
                            </p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                              {progress.percentage.toFixed(1)}%
                            </p>
                          </div>
                          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                            <PieChart className="w-6 h-6 text-purple-600" />
                          </div>
                        </div>
                        <Progress
                          value={progress.percentage}
                          className="h-2 mt-2"
                        />
                      </CardContent>
                    </Card>
                  </div>

                  {/* Budget Information Grid */}
                  <div className="grid grid-cols-2 gap-6">
                    {/* Left Column - Details */}
                    <Card>
                      <CardHeader className="border-b bg-white">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          Budget Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          {budget.Department && (
                            <div className="flex items-start">
                              <span className="text-sm font-medium text-gray-500 w-32">
                                Department:
                              </span>
                              <span className="text-sm text-gray-900 font-medium">
                                {budget.Department.name}
                              </span>
                            </div>
                          )}
                          {budget.Project && (
                            <div className="flex items-start">
                              <span className="text-sm font-medium text-gray-500 w-32">
                                Project:
                              </span>
                              <span className="text-sm text-gray-900 font-medium">
                                {budget.Project.name}
                              </span>
                            </div>
                          )}
                          {budget.Account && (
                            <div className="flex items-start">
                              <span className="text-sm font-medium text-gray-500 w-32">
                                Account:
                              </span>
                              <span className="text-sm text-gray-900 font-medium">
                                {budget.Account.code} - {budget.Account.name}
                              </span>
                            </div>
                          )}
                          <div className="flex items-start">
                            <span className="text-sm font-medium text-gray-500 w-32">
                              Status:
                            </span>
                            <Badge variant="outline" className="capitalize">
                              {budget.status || "Active"}
                            </Badge>
                          </div>
                          {budget.description && (
                            <div className="pt-4 border-t">
                              <span className="text-sm font-medium text-gray-500 block mb-2">
                                Description:
                              </span>
                              <p className="text-sm text-gray-700 leading-relaxed">
                                {budget.description}
                              </p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Right Column - Timeline */}
                    <Card>
                      <CardHeader className="border-b bg-white">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          Budget Timeline
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Calendar className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">
                                Start Date
                              </p>
                              <p className="text-sm font-semibold text-gray-900">
                                {new Date(
                                  budget.period_start,
                                ).toLocaleDateString("en-US", {
                                  weekday: "short",
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                              <Calendar className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">
                                End Date
                              </p>
                              <p className="text-sm font-semibold text-gray-900">
                                {new Date(budget.period_end).toLocaleDateString(
                                  "en-US",
                                  {
                                    weekday: "short",
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  },
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="pt-4 border-t">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <Target className="w-6 h-6 text-green-600" />
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Duration
                                </p>
                                <p className="text-sm font-semibold text-gray-900">
                                  {Math.ceil(
                                    (new Date(budget.period_end) -
                                      new Date(budget.period_start)) /
                                      (1000 * 60 * 60 * 24),
                                  )}{" "}
                                  days
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Budget Line Items */}
                  <Card>
                    <CardHeader className="border-b bg-white">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          Budget Line Items
                          <Badge variant="outline" className="ml-2">
                            {items.length} items
                          </Badge>
                        </CardTitle>
                        {budget.status === "draft" && (
                          <Button
                            size="sm"
                            onClick={() => {
                              onOpenChange(false);
                              onManageItems(budget, items);
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add Item
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {items.length === 0 ? (
                        <div className="text-center py-12">
                          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500 font-medium">
                            No budget items added yet
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            Add items to track your budget breakdown
                          </p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50">
                                <TableHead className="font-semibold">
                                  Item
                                </TableHead>
                                <TableHead className="font-semibold">
                                  Category
                                </TableHead>
                                <TableHead className="text-right font-semibold">
                                  Qty
                                </TableHead>
                                <TableHead className="text-right font-semibold">
                                  Unit Price
                                </TableHead>
                                <TableHead className="text-right font-semibold">
                                  Total
                                </TableHead>
                                {budget.status === "draft" && (
                                  <TableHead className="text-right font-semibold">
                                    Actions
                                  </TableHead>
                                )}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {items.map((item, index) => (
                                <TableRow
                                  key={item.id}
                                  className={
                                    index % 2 === 0
                                      ? "bg-white"
                                      : "bg-gray-50/50"
                                  }
                                >
                                  <TableCell>
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {item.name}
                                      </p>
                                      {item.description && (
                                        <p className="text-xs text-gray-500 mt-1">
                                          {item.description}
                                        </p>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      {item.category || "General"}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right text-gray-700">
                                    {item.quantity}
                                  </TableCell>
                                  <TableCell className="text-right font-medium text-gray-900">
                                    {financeService.formatCurrency(
                                      item.unit_price,
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold text-gray-900">
                                    {financeService.formatCurrency(
                                      item.quantity * item.unit_price,
                                    )}
                                  </TableCell>
                                  {budget.status === "draft" && (
                                    <TableCell className="text-right">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onDeleteItem(item.id)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                              <TableRow className="bg-blue-50 border-t-2 border-blue-200">
                                <TableCell
                                  colSpan={budget.status === "draft" ? 4 : 3}
                                  className="text-right font-bold text-gray-900"
                                >
                                  Total Budget Amount:
                                </TableCell>
                                <TableCell className="text-right font-bold text-blue-600 text-lg">
                                  {financeService.formatCurrency(
                                    items.reduce(
                                      (sum, item) =>
                                        sum + item.quantity * item.unit_price,
                                      0,
                                    ),
                                  )}
                                </TableCell>
                                {budget.status === "draft" && <TableCell />}
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="border-t bg-white p-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BudgetDetailsModal;
